import { useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateActionStatus, ACTION_STATUS } from "@core/features/actions";
import {
  useOnboardingActions,
  useOnboardingStepper,
} from "@core/features/onboarding";
import type { OnboardingStep } from "@core/features/onboarding";
import type { StepState } from "@core/features/onboarding";
import { usePlatformStore, useOnboarding } from "@platform/store";

type FlowType = "participant" | "orgAdmin";

type UseOnboardingPageArgs = {
  programId: number | undefined;
  flowType: FlowType;
  completionPath: string;
};

type UseOnboardingPageResult = {
  isLoading: boolean;
  programId: number | undefined;
  steps: OnboardingStep[];
  currentStep: OnboardingStep | null;
  effectiveStepIndex: number;
  isLastStep: boolean;
  canSkip: boolean;
  stepStates: StepState[];
  onStepComplete: () => void;
  onSkipClick: () => void;
  onStepClick: (stepIndex: number) => void;
  onBackClick: () => void;
};

/**
 * Shared hook for onboarding page logic.
 * Handles step navigation, URL/Zustand sync, and completion.
 */
export function useOnboardingPage({
  programId,
  flowType,
  completionPath,
}: UseOnboardingPageArgs): UseOnboardingPageResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = usePlatformStore((s) => s.user);
  const {
    setCurrentStep,
    skipStep,
    currentStepByProgram,
    skippedStepsByProgram,
  } = useOnboarding();

  const { steps, isLoading, currentFlow } = useOnboardingActions({
    programId: programId ?? 0,
    user,
    flowType,
  });

  const onboardingActionId = currentFlow?.onboardingAction?.id;

  const { mutateAsync: completeOnboarding } = useUpdateActionStatus({
    actionId: onboardingActionId ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: currentFlow?.onboardingAction?.current_status?.name,
  });

  const urlStep = searchParams.get("step");

  const { effectiveStepIndex, currentStep, isLastStep, canSkip, stepStates } =
    useOnboardingStepper({
      programId: programId ?? 0,
      steps,
      currentStepByProgram,
      skippedStepsByProgram,
      urlStep,
    });

  const zustandStep = programId ? currentStepByProgram[programId] ?? 0 : 0;

  // Initialize Zustand from API-computed currentStepIndex on first load
  useEffect(() => {
    if (
      !isLoading &&
      programId &&
      urlStep === null &&
      currentStepByProgram[programId] === undefined &&
      currentFlow?.currentStepIndex !== undefined
    ) {
      setCurrentStep(programId, currentFlow.currentStepIndex);
    }
  }, [
    isLoading,
    programId,
    urlStep,
    currentStepByProgram,
    currentFlow?.currentStepIndex,
    setCurrentStep,
  ]);

  // Sync URL param to Zustand when URL changes
  useEffect(() => {
    if (programId && urlStep !== null) {
      const stepNum = parseInt(urlStep, 10);
      if (!isNaN(stepNum) && stepNum !== zustandStep) {
        setCurrentStep(programId, stepNum);
      }
    }
  }, [urlStep, programId, zustandStep, setCurrentStep]);

  const handleStepComplete = useCallback(async () => {
    if (!programId) return;

    const nextStepIndex = effectiveStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setSearchParams({ step: String(nextStepIndex) });
      setCurrentStep(programId, nextStepIndex);
    } else {
      if (onboardingActionId) {
        await completeOnboarding();
      }
      navigate(completionPath, { replace: true });
    }
  }, [
    programId,
    effectiveStepIndex,
    steps.length,
    setSearchParams,
    setCurrentStep,
    onboardingActionId,
    completeOnboarding,
    navigate,
    completionPath,
  ]);

  const handleSkipClick = useCallback(async () => {
    if (!programId) return;

    skipStep(programId, effectiveStepIndex);

    const nextStepIndex = effectiveStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setSearchParams({ step: String(nextStepIndex) });
      setCurrentStep(programId, nextStepIndex);
    } else {
      if (onboardingActionId) {
        await completeOnboarding();
      }
      navigate(completionPath, { replace: true });
    }
  }, [
    programId,
    effectiveStepIndex,
    skipStep,
    steps.length,
    setSearchParams,
    setCurrentStep,
    onboardingActionId,
    completeOnboarding,
    navigate,
    completionPath,
  ]);

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (!programId) return;
      setSearchParams({ step: String(stepIndex) });
      setCurrentStep(programId, stepIndex);
    },
    [programId, setSearchParams, setCurrentStep]
  );

  const handleBackClick = useCallback(() => {
    if (!programId || effectiveStepIndex === 0) return;

    const prevStepIndex = effectiveStepIndex - 1;
    setSearchParams({ step: String(prevStepIndex) });
    setCurrentStep(programId, prevStepIndex);
  }, [programId, effectiveStepIndex, setSearchParams, setCurrentStep]);

  return {
    isLoading,
    programId,
    steps,
    currentStep,
    effectiveStepIndex,
    isLastStep,
    canSkip,
    stepStates,
    onStepComplete: handleStepComplete,
    onSkipClick: handleSkipClick,
    onStepClick: handleStepClick,
    onBackClick: handleBackClick,
  };
}
